import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { format, isValid } from 'date-fns';

import { Box, Button, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';

import {
  DatarouterError,
  useRentalManagerUserPropertySpecialOfferWebApiHandler_delete,
} from '@zg-rentals/rental-manager-env';

import PropertiesActions from '../../../../actions/PropertiesActions';

import type { SpecialOfferProps } from '../../../../model/SpecialOffer';
import { logError } from '@zg-rentals/log-error';

type Props = {
  isInPostingPath?: boolean;
  onEditOfferClick?: (action: string) => void;
  isFeed: boolean;
  listingAlias: string;
  specialOffer: SpecialOfferProps;
};

const SpecialOfferItem = ({
  isInPostingPath = false,
  onEditOfferClick = () => {},
  isFeed,
  listingAlias,
  specialOffer,
}: Props) => {
  const { startDateYyyyMmDd: startDate, endDateYyyyMmDd: endDate, description, offerIdEncoded } = specialOffer;

  const { enqueueToast } = useToast();
  const dispatch = useDispatch();

  const { mutate: onDelete, isPending: isRMEDeleteLoading } =
    useRentalManagerUserPropertySpecialOfferWebApiHandler_delete({
      onSuccess: () => {
        dispatch(PropertiesActions.updateCurrentProperty({ specialOffers: [] }));
      },
      onError: (e) => {
        const message = DatarouterError.getDisplayMessage(e);

        logError({
          error: e,
          context: {
            datarouterMessage: message,
            details: 'PropertiesActions.deleteSpecialOffer',
          },
        });

        enqueueToast(<Toast tone="critical">There was an error deleting the offer.</Toast>, {
          duration: 10000,
        });
      },
    });

  const handleEditOfferClick = (action: string) => onEditOfferClick(action);

  const handleRemoveOfferClick = () =>
    onDelete({
      getParams: {
        listingAlias,
        offerIdEncoded: offerIdEncoded || '',
      },
    });

  const formattedStartDate = useMemo(
    () =>
      isValid(new Date(`${startDate}T10:00:00`)) ? format(new Date(`${startDate}T10:00:00`), 'MMM dd, yyyy') : ' - ',
    [startDate],
  );
  const formattedEndDate = useMemo(
    () => (isValid(new Date(`${endDate}T10:00:00`)) ? format(new Date(`${endDate}T10:00:00`), 'MMM dd, yyyy') : ' - '),
    [endDate],
  );

  const renderOfferDateDisplayText = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ startDate, endDate, formattedStartDate, formattedEndDate }: any) => {
      let offerDateDisplayText = '';

      switch (true) {
        case Boolean(!startDate && !endDate):
          offerDateDisplayText = 'On going';
          break;
        case Boolean(!startDate && endDate):
          offerDateDisplayText = `Until ${formattedEndDate}`;
          break;
        case Boolean(startDate && !endDate):
          offerDateDisplayText = `${formattedStartDate} - ongoing`;
          break;
        default:
          offerDateDisplayText = `${formattedStartDate} - ${formattedEndDate}`;
          break;
      }

      return (
        <Text textStyle={isInPostingPath ? 'body-bold' : 'body-sm-bold'} css={{ marginBottom: '200' }} asChild>
          <p>{offerDateDisplayText}</p>
        </Text>
      );
    },
    [],
  );

  return (
    <div data-testid="SpecialOfferDisplay">
      <Box css={{ marginTop: '200', marginBottom: isInPostingPath ? '800' : '200' }}>
        {renderOfferDateDisplayText({ startDate, endDate, formattedStartDate, formattedEndDate })}
        <Text css={{ wordBreak: 'break-word', whiteSpace: 'break-spaces' }} asChild>
          <p>{description}</p>
        </Text>
      </Box>
      <Box css={{ display: 'flex' }}>
        {isInPostingPath && (
          <Button
            onClick={() => handleEditOfferClick('edit')}
            tone="neutral"
            emphasis="outlined"
            size="md"
            css={{ marginRight: '800' }}
          >
            Edit offer details
          </Button>
        )}

        {!isFeed && (
          <TextButton
            icon={<IconDeleteOutline />}
            onClick={handleRemoveOfferClick}
            disabled={isRMEDeleteLoading}
            tone={isInPostingPath ? 'neutral' : 'brand'}
          >
            Remove offer
          </TextButton>
        )}
      </Box>
    </div>
  );
};

export default SpecialOfferItem;
